import type { ReactElement } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import type { UserRole } from '@proctoring/shared';
import { FullPageMessage } from '../components/FullPageMessage.js';
import { useAuth } from './AuthProvider.js';

/** Where a signed-in user belongs when they land on `/` or on a route for another role. */
export function homePathForRole(role: UserRole): string {
  return role === 'candidate' ? '/candidate' : '/interviewer';
}

export interface RequireAuthProps {
  /** When given, a signed-in user outside these roles is bounced to their own home. */
  roles?: readonly UserRole[];
}

/**
 * Gates a route subtree on an authenticated session.
 *
 * Rendering a spinner during `bootstrapping` rather than redirecting is what keeps a deep
 * link (an emailed interview URL, a bookmarked dashboard) intact across a page reload —
 * redirecting first would drop the location before `/auth/me` had a chance to answer.
 */
export function RequireAuth({ roles }: RequireAuthProps): ReactElement {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'bootstrapping') {
    return <FullPageMessage title="Restoring your session…" />;
  }

  if (status === 'anonymous' || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to={homePathForRole(user.role)} replace />;
  }

  return <Outlet />;
}

/** The inverse guard: keeps a signed-in user off /login and /setup-password. */
export function RedirectIfAuthenticated(): ReactElement {
  const { status, user } = useAuth();

  if (status === 'bootstrapping') {
    return <FullPageMessage title="Restoring your session…" />;
  }
  if (status === 'authenticated' && user) {
    return <Navigate to={homePathForRole(user.role)} replace />;
  }
  return <Outlet />;
}
